import { ShotExtra } from '../../store/Session/SessionModel';

/**
 * Lateral (x = side) vs down-range (y = carry) scatter in consistent units. Carry and
 * side are independent orthogonal components (carry is forward distance, side the lateral
 * offset), so the landing point is (side, carry) — every shot that passes the table
 * filters can be plotted, including mishits where |side| > carry (KWSA-13, KWSA-30).
 *
 * Both axes read the mode-aware converted values (carryDistanceConverted /
 * sideConverted). In Normalized mode a shot without normalized values renders as
 * "---" in the tables, so it must be dropped from the dispersion too — matching the
 * Golf App, which plots only the shots that have a normalized position (KWSA-27).
 */
export function chartXYFromShot(shot: ShotExtra): { x: number; y: number } | null {
  const carryConverted = shot.carryDistanceConverted;
  const sideConverted = shot.sideConverted;

  // No displayable value in the current mode (e.g. no normalized value) → not plotted.
  if (carryConverted?.valueString === '---' || sideConverted?.valueString === '---') {
    return null;
  }

  const carryDistance = carryConverted?.value !== undefined ? carryConverted.value : shot.carryDistance;
  if (!Number.isFinite(carryDistance)) {
    return null;
  }
  const side = sideConverted?.value !== undefined ? sideConverted.value : shot.side;
  if (!Number.isFinite(side)) {
    return null;
  }
  // Carry is the down-range distance and side an independent lateral offset, so the
  // landing point is simply (side, carry). Deriving y as sqrt(carry² − side²) wrongly
  // treated carry as the hypotenuse, so a mishit where |side| > carry collapsed onto the
  // 0-yard line (e.g. carry 21 / side 29 → y 0) instead of plotting at its carry — KWSA-30.
  return { x: side, y: carryDistance };
}
